<template>

    <div v-if="showMe">
        <v-row id="sd-widget-header-row">
            <v-col class="text-center pt-0">
                <h3>Sudden Death</h3>
                <div id="sd-widget-header-sub1" class="font-italic">Just pick a winner. Easy*.</div>
                <div id="sd-widget-header-sub2">* Except once you've picked a team, you can't pick them again.</div>
            </v-col>
        </v-row>
        <v-row>
           <v-col id="sd-widget-game-info-row" class="text-center">
               Game {{ currentGame.game }} <span class="double-pipe mx-4">||</span> Round {{ getRound }}
           </v-col>
        </v-row>
        <v-row id="sd-widget-game-history-row">
            <v-col id="sd-widget-game-history-col" class="text-center py-1" v-html="getHistory"></v-col>
        </v-row>
        <v-row id="sd-widget-pick-row" v-if="!deadlineHasPassed && isAlive">
            <v-col class="team-item-col" :class="{ 'team-item-selected': team.team === getTeamPicked }" cols="auto" v-for="team in selectableTeams" :key="team.abbrv">

              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <div 
                    v-bind="attrs"
                    v-on="on"
                    @click="updatePick(team.team)"
                  >{{ team.abbrv }}</div>
                </template>
                <span>{{ team.team }}</span>
              </v-tooltip>

            </v-col>
        </v-row>
        <v-row v-else class="mt-2">
            <v-col class="text-center" v-if="isAlive">
                Team picked: {{ getTeamPicked }}
            </v-col>
            <v-col class="text-center" v-else>
                You're Dead!
            </v-col>
        </v-row>


    </div>

</template>

<script>

  import {mapGetters} from "vuex";
  import moment from 'moment';

  export default {
    name: "SuddenDeathWidget",
    props: [
      //
    ],
    components: {
      //
    },
    data: () => ({
      showMe: false,
      pickedTeam: null,
    }),
    computed: {
      ...mapGetters([
        'currentGame',
        'weekGames',
        'weekInfo',
        'weeks',
        'user'
      ]),
      thisWeekTeams() {
        if (this.weekGames) {
          let homes = [];
          let aways = [];
          this.weekGames.forEach(game => {
            homes.push({
              'abbrv': game.home_abbrv.abbrv,
              'team': game.home_team
            })
            aways.push({
              'abbrv': game.away_abbrv.abbrv,
              'team': game.away_team
            })
          })
          return [...homes, ...aways];
        }
      },
      previouslySelectedTeams() {
        if (this.userPicks) {
            return this.userPicks.filter( pick => pick.week_id != this.weekInfo.id ).map( pick => pick.team_picked );
        }
      },
      selectableTeams() {
        if (this.thisWeekTeams && this.previouslySelectedTeams) {
          const rtn = this.thisWeekTeams.filter( team => !this.previouslySelectedTeams.includes(team.team) );
          return rtn.sort((a,b) => (a.abbrv > b.abbrv) ? 1 : ((b.abbrv > a.abbrv) ? -1 : 0))
        }
      },
      userPicks() {
        if (this.currentGame) {
            return this.currentGame.picks.filter(pick => pick.user_id === this.user.id);
        }
      },
      deadline() {
        if (this.weekGames) {
          const mindate =  Math.min.apply(Math, this.weekGames.map( obj => moment(obj.kickoff_datetime)))
          return moment(mindate);
        }
      },
      deadlineHasPassed() {
        let now = moment();
        return now > this.deadline;
      },
      isAlive() {
        //either: first week of game OR prev week pick was a winner
        if (this.currentGame.start_week_id === this.weekInfo.id) {
          return true;
        } else {
          if (!this.userPicks) {
            return false;
          } else {
            let prev = this.currentGame.picks.find(pick => pick.user_id === this.user.id && this.getPlayWeek(pick.week_id) === this.weekInfo.play_week_num - 1);
            if (!prev || prev.result !== 'won') {
              return false;
            } else {
              return true;
            }
          }
        }
      },
      getTeamPicked() {
        if (this.weekInfo && this.userPicks) {
          let pick = this.userPicks.filter(pick => pick.week_id === this.weekInfo.id).map(pickedPick => pickedPick.team_picked)[0];
          return pick ? pick : 'No Team Picked';
        }
      },
      getRound() {
        if (this.currentGame && this.weekInfo) {
          return (this.weekInfo.play_week_num - this.getPlayWeek(this.currentGame.start_week_id)) + 1
        }
      },
      getHistory() {
        if (this.userPicks && this.userPicks.length) {
          const pickArr = [];
          const maxPickWeek =  Math.max.apply(Math, this.userPicks.map( pick => pick.week_id ));
          const maxPickPlayWeek = this.getPlayWeek(maxPickWeek);
          let lastResult = 'lost';
          this.userPicks.forEach(pick => {
            if (pick.result) {
              lastResult = pick.result;
              pickArr.push(`Week ${ this.getPlayWeek(pick.week_id) }: ${ pick.team_picked } (${ pick.result })`);
            } else {
              pickArr.push(`Week ${ this.getPlayWeek(pick.week_id) }: ${ pick.team_picked } (tbc)`);
            }
          })
          if ((this.getPlayWeek(this.weekInfo.id) - maxPickPlayWeek) > 1 && lastResult === 'won') {
            pickArr.push(`Week ${ this.getPlayWeek(maxPickWeek + 1) }: No team picked (lost)`);
          }
          return pickArr.join('<span style="margin: 0 0.5rem; font-size: 1.2rem;">&#8594;</span>');
        } else {
          if (this.deadlineHasPassed) {
            return 'No team picked. Oh dear.' 
          } else {
            return 'Quick! Pick a winner!'
          }
        }
      }
    },
    methods: {
      setup() {
        this.pickedTeam = this.getTeamPicked;
        this.showMe = true;
      },
      getPlayWeek(week_id) {
        if (this.weeks) {
          return this.weeks.find(week => week.id === week_id).play_week_num;
        }
      },
      updatePick(team) {
        let data = {
          'sudden_death_id': this.currentGame.id,
          'week_id': this.weekInfo.id,
          'team_picked': team
        }
        this.$store.dispatch('updatePick', data);
      }
    },
    created() {

      if (this.currentGame) {
        this.setup();
      }

      this.unwatchgame = this.$store.watch(
        (state, getters) => getters.currentGame,
        (newValue, oldValue) => {
          this.setup()
        }
      )

    },beforeDestroy() {
      this.unwatchgame();
    }
  }
</script>

<style scoped>

    #sd-widget-header-row {
        border-bottom: 1px solid #cccccc;
    }

    #sd-widget-header-sub1 {
        color: var(--v-secondary-base);
        /*font-size: 0.7rem;*/
    }

    #sd-widget-header-sub2 {
        font-size: 0.7rem;
    }

    #sd-widget-game-info-row {
        color: var(--v-secondary-base);
        background-color: #dddddd;
        border-bottom: 1px solid #cccccc;
    }

    #sd-widget-game-history-row {
        background-color: #dddddd;
        border-bottom: 1px solid #cccccc;
        font-size: 0.85rem;
    }

    .right-arrow {
        margin: 0 3rem;
        font-size: 5rem;
    }

    #sd-widget-pick-row {
      padding-top: 0.3rem;
      padding-bottom: 0.3rem;
    }

    .team-item-col {
      margin: 0.3rem;
      padding: 0.3rem 0.5rem;
      border-radius: 5px;
      color: #ffffff;
      background-color: var(--v-secondary-base);
    }

    .team-item-selected {
      background-color: var(--v-primary-base);
      color: var(--v-secondary-base);
    }

</style>
